import { BitkitAlert, BitkitButton, BitkitCodeSnippet, BitkitDialog } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useRef } from 'react';

import { CIConfigExpertAvailability } from '@/pages/WorkflowEditorPage/WorkflowEditorPage.store';

import { CIConfigExpertOptInDismissMethod } from './ciConfigExpertTracking';

const DOCS_URL = 'https://docs.bitrise.io/en/bitrise-ci/workflows-and-pipelines/ai-configuration-assistant';

export type CIConfigExpertOptOutReason = Extract<
  CIConfigExpertAvailability,
  'disabled-by-project' | 'disabled-by-workspace'
>;

// Who to ask is a property of the level the assistant is switched off at.
const ASK_SOMEONE_ELSE: Record<CIConfigExpertOptOutReason, string> = {
  'disabled-by-project': 'To turn it on for this project, send the URL to a project admin.',
  'disabled-by-workspace': 'To turn it on for this workspace, send the URL to a workspace admin or owner.',
};

// Named only where the reader can act on it. A reader who has to ask someone else is told about the
// project in front of them either way -- which level holds the switch is the next sentence's job.
const TURNED_OFF_AT: Record<CIConfigExpertOptOutReason, string> = {
  'disabled-by-project': 'The AI Assistant is turned off for this project.',
  'disabled-by-workspace': 'The AI Assistant is turned off for this Workspace.',
};

type Props = {
  open: boolean;
  /** Fires for every user-initiated close except a successful opt-in, which closes programmatically. */
  onDismiss: (method: CIConfigExpertOptInDismissMethod) => void;
  onDocsClick: () => void;
  /** Fires when the exit animation finishes, so the owner can unmount the dialog. */
  onExitComplete?: () => void;
  reason: CIConfigExpertOptOutReason;
  canOptIn: boolean;
  isOptingIn: boolean;
  onOptIn: () => void;
  /** Absolute URL of the settings page owning the toggle, to hand to whoever can flip it. */
  settingsUrl: string;
};

const CIConfigExpertOptInDialog = ({
  open,
  onDismiss,
  onDocsClick,
  onExitComplete,
  reason,
  canOptIn,
  isOptingIn,
  onOptIn,
  settingsUrl,
}: Props) => {
  const dismissRef = useRef<HTMLElement>(null);
  const turnOnRef = useRef<HTMLElement>(null);
  const hasWarning = canOptIn && reason === 'disabled-by-workspace';

  return (
    <BitkitDialog
      title="AI Assistant"
      open={open}
      onOpenChange={({ open: next }) => {
        if (!next) onDismiss('close_button');
      }}
      onExitComplete={onExitComplete}
      state={isOptingIn ? 'loading' : undefined}
      // Without this the header's close button takes focus, which says nothing about what to do next.
      // Everywhere except the warning: there the action reaches past this project, and one stray Enter
      // shouldn't switch Bitrise AI on for the whole workspace.
      initialFocusEl={() => (canOptIn && !hasWarning ? turnOnRef.current : dismissRef.current)}
      footerContent={
        // Only the workspace switch reaches past this project. Warning the user about the reversible,
        // project-scoped one as well would weaken the one that matters.
        hasWarning ? (
          <BitkitAlert
            variant="warning"
            messageText="Turning this on enables Bitrise AI for the whole workspace, not just this project."
          />
        ) : undefined
      }
      footerButtons={
        <>
          {/* The footer row is right-aligned, so the auto margin is what parks the docs on the left. */}
          <BitkitButton variant="tertiary" href={DOCS_URL} isExternal marginInlineEnd="auto" onClick={onDocsClick}>
            View documentation
          </BitkitButton>
          {/* Not an ActionTrigger: reporting the dismissal to the owner is what closes the dialog, so
            the shell's onOpenChange (which can't tell the close routes apart) never fires for it. */}
          <BitkitButton
            ref={dismissRef}
            variant="secondary"
            state={isOptingIn ? 'disabled' : undefined}
            onClick={() => onDismiss('cancel_button')}
          >
            {canOptIn ? 'Cancel' : 'Dismiss'}
          </BitkitButton>
          {/* Nothing to promise when the user can't act: the snippet below carries its own copy button,
            so a primary here would only offer what the dialog already gives away. */}
          {canOptIn && (
            <BitkitButton ref={turnOnRef} state={isOptingIn ? 'loading' : undefined} onClick={onOptIn}>
              Turn on
            </BitkitButton>
          )}
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap="16">
        {canOptIn ? (
          <Text>{TURNED_OFF_AT[reason]} Turn it on to get help with your config.</Text>
        ) : (
          // Who to ask comes right after what the feature is: for a non-admin this dialog is often the
          // only place they learn it exists, and the link below is what they have to pass on.
          <>
            <Text>
              The AI Assistant is turned off for this project. It helps with your config.
              <br />
              {ASK_SOMEONE_ELSE[reason]}
            </Text>
            <BitkitCodeSnippet variant="single">{settingsUrl}</BitkitCodeSnippet>
          </>
        )}
      </Box>
    </BitkitDialog>
  );
};

export default CIConfigExpertOptInDialog;
